import React from 'react';
import { useTranslation } from 'react-i18next';

// Import service images
import airportTransfer from '../assets/services/airport_transfer.jpg';
import distressedPassengers from '../assets/services/distressed.jpeg';
import anyOccasionCar from '../assets/services/car_service.webp';
import hotelTransfer from '../assets/services/hotel_transfer.jpeg';
import flightAndCabinCrew from '../assets/services/flight_crew_shuttle.jpg';
import companyShuttle from '../assets/services/company_service.jpg';
import charterServices from '../assets/services/charter_service.webp';

interface Service {
  id: string;
  titleKey: string;
  bullet1Key: string;
  bullet2Key: string;
  image: string;
}

interface ServicesGridProps {
  className?: string;
}

const ServicesGrid: React.FC<ServicesGridProps> = ({ className = '' }) => {
  const { t } = useTranslation();

  const services: Service[] = [
    {
      id: 'airport-transfer',
      titleKey: 'service_airport_transfer_title',
      bullet1Key: 'service_airport_transfer_bullet1',
      bullet2Key: 'service_airport_transfer_bullet2',
      image: airportTransfer
    },
    {
      id: 'distressed-passengers',
      titleKey: 'service_distressed_passengers_title',
      bullet1Key: 'service_distressed_passengers_bullet1',
      bullet2Key: 'service_distressed_passengers_bullet2',
      image: distressedPassengers
    },
    {
      id: 'any-occasion',
      titleKey: 'service_any_occasion_title',
      bullet1Key: 'service_any_occasion_bullet1',
      bullet2Key: 'service_any_occasion_bullet2',
      image: anyOccasionCar
    },
    {
      id: 'hotel-transfer',
      titleKey: 'service_hotel_transfer_title',
      bullet1Key: 'service_hotel_transfer_bullet1',
      bullet2Key: 'service_hotel_transfer_bullet2',
      image: hotelTransfer
    },
    {
      id: 'flight-cabin-crew',
      titleKey: 'service_flight_cabin_crew_title',
      bullet1Key: 'service_flight_cabin_crew_bullet1',
      bullet2Key: 'service_flight_cabin_crew_bullet2',
      image: flightAndCabinCrew
    },
    {
      id: 'company-shuttle',
      titleKey: 'service_company_shuttle_title',
      bullet1Key: 'service_company_shuttle_bullet1',
      bullet2Key: 'service_company_shuttle_bullet2',
      image: companyShuttle
    },
    {
      id: 'charter-services',
      titleKey: 'service_charter_services_title',
      bullet1Key: 'service_charter_services_bullet1',
      bullet2Key: 'service_charter_services_bullet2',
      image: charterServices
    }
  ];

  return (
    <div className={`services-section ${className}`}>
      <style>
        {`
          .services-section {
            position: relative;
            width: 100%;
            padding: 4rem 0;
            background: #fff;
          }

          .services-header {
            max-width: 1600px;
            margin: 0 auto 2.5rem;
            padding: 0 2rem;
            text-align: left;
          }

          .services-title {
            font-size: 2.5rem;
            font-weight: 700;
            color: #000;
            margin: 0 0 1rem 0;
          }

          .services-subtitle {
            font-size: 1.125rem;
            line-height: 1.6;
            color: #555;
            margin: 0;
            max-width: 800px;
          }

          .services-grid-container {
            max-width: 1600px;
            margin: 0 auto;
            padding: 0 2rem;
          }

          .services-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 2rem;
          }

          .service-card {
            display: flex;
            flex-direction: column;
            background: #fff;
            border-radius: 16px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            overflow: hidden;
            height: 100%;
            border: 1px solid rgba(0, 0, 0, 0.05);
          }

          .service-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
          }

          .service-image-wrapper {
            position: relative;
            width: 100%;
            height: 240px;
            overflow: hidden;
          }

          .service-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            transition: transform 0.3s ease;
          }

          .service-card:hover .service-image {
            transform: scale(1.05);
          }

          .service-content {
            padding: 2rem 1.5rem;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
          }

          .service-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: #000;
            margin: 0 0 1rem 0;
            position: relative;
            padding-bottom: 0.75rem;
          }

          .service-title:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 60px;
            height: 3px;
            background: var(--gold);
            border-radius: 2px;
          }

          .service-bullets {
            list-style: none;
            padding: 0;
            margin: 1rem 0 0 0;
            flex-grow: 1;
          }

          .service-bullet {
            position: relative;
            padding-left: 1.5rem;
            margin-bottom: 1rem;
            font-size: 1rem;
            line-height: 1.6;
            color: #555;
          }

          .service-bullet:before {
            content: "•";
            position: absolute;
            left: 0;
            top: 0;
            color: var(--gold);
            font-weight: bold;
            font-size: 1.2rem;
          }

          .service-bullet:last-child {
            margin-bottom: 0;
          }

          /* Tablet Styles */
          @media (max-width: 1200px) {
            .services-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .services-section {
              padding: 3rem 0;
            }
            
            .services-grid-container {
              padding: 0 1rem;
            }

            .services-grid {
              grid-template-columns: 1fr;
              gap: 1.5rem;
            }

            .service-card {
              max-width: 100%;
            }

            .service-image-wrapper {
              height: 200px;
            }

            .service-content {
              padding: 1.5rem 1rem;
            }

            .service-title {
              font-size: 1.3rem;
            }

            .service-bullet {
              font-size: 0.9rem;
              margin-bottom: 0.75rem;
            }
          }
        `}
      </style>

      <div className="services-grid-container">
        <div className="services-grid">
          {services.map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-image-wrapper">
                <img
                  src={service.image}
                  alt={String(t(service.titleKey))}
                  className="service-image"
                />
              </div>
              <div className="service-content">
                <h3 className="service-title">{t(service.titleKey)}</h3>
                <ul className="service-bullets">
                  <li className="service-bullet">{t(service.bullet1Key)}</li>
                  <li className="service-bullet">{t(service.bullet2Key)}</li>
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServicesGrid; 